import { useQuery } from '@tanstack/react-query';
import { Header } from 'components';
import { TEXT_STRING, queryKeys } from '../../../constants';
import { getGachaTickets } from 'api/gacha-tickets';
import { useUserStore } from 'store';
import { GachaTicket } from 'types/gacha-tickets';
import { groupTickets, isUrgent, ticketDate, ticketExpiry, ticketKind, ticketName } from './wallet';
const str = TEXT_STRING.GACHA_TICKETS;
const labelClass = 'text-[11px] tracking-[.18em] text-[#756e64]';

function Expiry({ ticket, showDate = false, mark = true }: { ticket: GachaTicket; showDate?: boolean; mark?: boolean }) {
  const urgent = isUrgent(ticket);
  return (
    <span data-testid="ticket-expiry" className={`inline-flex flex-wrap items-center gap-x-[6px] ${urgent ? 'text-primary-700' : 'text-[#756e64]'}`}>
      {urgent && mark && <i data-testid="urgency-mark" aria-hidden="true" className="inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-current" />}
      <span>{ticketExpiry(ticket)}</span>
      {showDate && ticket.remainingDays !== 0 && <span className="font-ticket-serif tracking-[.02em]">{ticketDate(ticket.expiresAt)}</span>}
    </span>
  );
}

function TicketGroup({ first, items }: Omit<ReturnType<typeof groupTickets>[number], 'key'>) {
  const expandable = items.length > 1;
  const Container = expandable ? 'details' : 'div';
  const Heading = expandable ? 'summary' : 'div';
  return (
    <Container role="group" aria-label={ticketName(first)} className="group border-b border-[#ece6db] py-4 first:pt-0">
      <Heading className={`list-none space-y-[6px] [&::-webkit-details-marker]:hidden ${expandable ? 'cursor-pointer' : ''}`}>
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-4">
          <h2 className="min-w-0 break-words font-ticket-serif text-[16px] font-medium leading-[1.5] [overflow-wrap:anywhere]">{ticketName(first)}</h2>
          <p data-testid="ticket-count" className="shrink-0 whitespace-nowrap"><span className="font-ticket-serif text-[22px] font-medium">{items.length}</span><span className="ml-[2px] text-[12px] text-[#756e64]">{str.UNIT}</span></p>
        </div>
        <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-[12px] text-[#756e64]">
          <span>{ticketKind(first)}</span><Expiry ticket={first} showDate />
        </div>
        {expandable && <p className="text-[11px] tracking-[.06em] text-[#756e64]">
          <span className="group-open:hidden">{str.DETAILS}</span><span className="hidden group-open:inline">{str.CLOSE}</span>
        </p>}
      </Heading>
      {expandable && <ul className="mt-[6px] space-y-[6px] border-t border-[#ece6db] pt-2 text-[12px] text-[#756e64]">
        {items.map((ticket) => <li key={ticket.ticketId} className="flex flex-wrap justify-between gap-x-3 gap-y-1">
          <span className="font-ticket-serif tracking-[.02em]">{ticketDate(ticket.expiresAt)}</span><Expiry ticket={ticket} />
        </li>)}
      </ul>}
    </Container>
  );
}

export default function GachaTickets() {
  const userId = useUserStore((state) => state.user?.id);
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: [queryKeys.SALON_GACHA_TICKETS, userId],
    queryFn: ({ signal }) => getGachaTickets(signal),
    enabled: !!userId,
    refetchOnWindowFocus: true,
  });
  const groups = groupTickets(data?.usable ?? []);
  return (
    <div className="h-full overflow-y-auto bg-white text-[#1f1d1a]">
      <Header title={TEXT_STRING.MY_PAGE.GACHA_TICKETS} />
      <main className="grid gap-[26px] px-[22px] pt-7 pb-20 text-[13px] leading-[1.6] lining-nums tabular-nums">
        {isPending ? (
          <p role="status" className={labelClass}>{str.LOADING}</p>
        ) : isError ? (
          <div role="alert" className="space-y-3 text-center text-[12px] text-[#756e64]">
            <p>{str.ERROR}</p>
            <button type="button" onClick={() => refetch()} className="underline underline-offset-4">{str.RETRY}</button>
          </div>
        ) : data && (<>
          {!!groups.length && <section aria-label={str.USABLE_COUNT.replace('$count', String(data.usable.length)) + (data.hasMoreUsable ? str.DISPLAYED_ONLY : '')} className="grid gap-1 border-b border-[#e3c9a6] pb-[18px]">
            <h1 className={labelClass}>{str.USABLE}</h1>
            <p className="flex items-baseline gap-[6px]"><span className="font-ticket-serif text-[44px] font-medium leading-[1.05]">{data.usable.length}</span><span className="text-[12px] text-[#756e64]">{str.UNIT}</span>{data.hasMoreUsable && <span className={labelClass}>{str.DISPLAYED_ONLY}</span>}</p>
            <p className="mt-1 text-[12px] text-[#756e64]">{str.NEAREST}　<Expiry ticket={groups[0].first} showDate mark={false} /></p>
          </section>}
          {data.hasMoreUsable && <p className={labelClass}>{str.OVERFLOW}</p>}
          {!groups.length && <div className={`grid justify-items-center gap-[14px] text-center ${data.past.length ? 'pt-[18px] pb-1' : 'pt-[72px] pb-16'}`}>
            <span aria-hidden="true" className="h-px w-7 bg-[#e3c9a6]" />
            <p className="font-ticket-serif text-[15px]">{data.past.length ? str.NO_USABLE : str.EMPTY}</p>
            <span aria-hidden="true" className="h-px w-7 bg-[#e3c9a6]" />
          </div>}
          {!!groups.length && <div>{groups.map((group) => <TicketGroup key={group.key} first={group.first} items={group.items} />)}</div>}
          {!!data.past.length && <section className="space-y-[10px]" aria-label={str.PAST}>
            <h2 className={labelClass}>{str.PAST}</h2>
            {data.past.length === 50 && <p className={labelClass}>{str.PAST_LIMIT}</p>}
            <div>{data.past.map((ticket) => {
              const date = ticket.status === 'USED' ? ticket.usedAt : ticket.expiresAt;
              return (
              <article key={ticket.ticketId} className="flex flex-wrap justify-between gap-x-3 gap-y-1 border-b border-[#ece6db] py-[9px] text-[12px] text-[#756e64]">
                <h3 className="min-w-0 break-words font-ticket-serif [overflow-wrap:anywhere]">{ticketName(ticket)}</h3>
                <p>{ticket.status === 'USED' ? str.USED : str.EXPIRED}　{date && <span className="font-ticket-serif tracking-[.02em]">{ticketDate(date)}</span>}</p>
              </article>
            ); })}</div>
          </section>}
        </>)}
      </main>
    </div>
  );
}
